/**
 * CampaignsSection - Retail Campaigns Display
 *
 * Shows retail partnership campaigns with purchase verification:
 * - Receipt upload (OCR)
 * - Code entry (alphanumeric from product)
 * - QR code scanning
 * - App deep links
 *
 * This implements the "Revolving Retail Competition" loop:
 * Purchase → Play → Unlock → Purchase
 */

import { useNavigate } from "react-router";
import { FaShoppingCart, FaReceipt, FaQrcode, FaMobileAlt, FaKeyboard, FaGift, FaCheckCircle } from "react-icons/fa";
import { HubRetailCampaign, RetailVerifyMethod } from "@/hooks/hub/useGetHub";
import "./CampaignsSection.scss";

interface CampaignsSectionProps {
    campaigns: HubRetailCampaign[];
}

/**
 * Get icon for verification method
 */
const VERIFY_ICONS: Record<RetailVerifyMethod, React.ReactNode> = {
    RECEIPT: <FaReceipt />,
    CODE: <FaKeyboard />,
    QR: <FaQrcode />,
    APP_LINK: <FaMobileAlt />,
};

/**
 * Get label for verification method
 */
const VERIFY_LABELS: Record<RetailVerifyMethod, string> = {
    RECEIPT: "Upload Receipt",
    CODE: "Enter Code",
    QR: "Scan QR Code",
    APP_LINK: "Open App",
};

export const CampaignsSection = ({ campaigns }: CampaignsSectionProps) => {
    const navigate = useNavigate();

    /*
     * A retail campaign delegates its purchase verification to an assigned
     * quest — the quest system owns the method-specific flow (receipt OCR,
     * code entry, QR scan, app link). The verify CTA therefore routes the
     * player to the quests surface where that quest lives, rather than
     * re-implementing verification here. A campaign with no questId is not
     * yet wired to a target, so its button is disabled rather than dead.
     */
    const handleVerify = (campaign: HubRetailCampaign) => {
        if (!campaign.questId) return;
        navigate("/play?view=quests");
    };

    if (campaigns.length === 0) {
        return (
            <section className="section campaigns-section">
                <div className="section__empty">
                    <FaShoppingCart size={48} />
                    <h3>Retail Campaigns Coming Soon</h3>
                    <p>Purchase products to unlock exclusive in-game rewards.</p>
                </div>
            </section>
        );
    }

    return (
        <section className="section campaigns-section">
            <div className="section__header">
                <h2>
                    <FaShoppingCart />
                    Retail Campaigns
                </h2>
                <p>Purchase at participating retailers to unlock exclusive rewards</p>
            </div>

            {/* How It Works */}
            <div className="campaigns-section__how-it-works">
                <h3>How It Works</h3>
                <div className="campaigns-section__steps">
                    <div className="campaigns-section__step">
                        <div className="campaigns-section__step-number">1</div>
                        <FaShoppingCart size={24} />
                        <span>Purchase Product</span>
                    </div>
                    <div className="campaigns-section__step-arrow">→</div>
                    <div className="campaigns-section__step">
                        <div className="campaigns-section__step-number">2</div>
                        <FaCheckCircle size={24} />
                        <span>Verify Purchase</span>
                    </div>
                    <div className="campaigns-section__step-arrow">→</div>
                    <div className="campaigns-section__step">
                        <div className="campaigns-section__step-number">3</div>
                        <FaGift size={24} />
                        <span>Unlock Rewards</span>
                    </div>
                </div>
            </div>

            {/* Campaign Cards */}
            <div className="campaigns-section__grid">
                {campaigns.map((campaign) =>
                    <div key={campaign._id} className="campaigns-section__card">
                        {/* Retailer Logo */}
                        <div className="campaigns-section__card-logo">
                            {campaign.logoImageUrl
                                ? <img src={campaign.logoImageUrl} alt={campaign.retailer} />
                                : <span>{campaign.retailer}</span>
                            }
                        </div>

                        {/* Campaign Info */}
                        <div className="campaigns-section__card-content">
                            <h3>{campaign.retailer}</h3>
                            {campaign.description && <p>{campaign.description}</p>}

                            {/* Reward Info */}
                            {campaign.rewardDescription &&
                                <div className="campaigns-section__card-reward">
                                    <FaGift />
                                    <span>{campaign.rewardDescription}</span>
                                </div>
                            }
                        </div>

                        {/* Verification CTA */}
                        <div className="campaigns-section__card-action">
                            <button
                                type="button"
                                className="btn btn--primary"
                                onClick={() => handleVerify(campaign)}
                                disabled={!campaign.questId}
                                title={campaign.questId ? undefined : "Verification opens soon"}
                            >
                                <span>
                                    {VERIFY_ICONS[campaign.verifyMethod]}
                                    {VERIFY_LABELS[campaign.verifyMethod]}
                                </span>
                            </button>
                        </div>
                    </div>)}
            </div>
        </section>
    );
};
